import React, { useState } from 'react';
import { Building2, User, Mail, Phone, Lock, Hash, ShieldCheck } from 'lucide-react';

const CompanyForm = ({ onSubmit, onCancel, loading }) => {
  const [formData, setFormData] = useState({
    name: '',
    industry: 'Enterprise Technology',
    logo: '',
    adminName: '',
    adminEmail: '',
    adminPhone: '',
    adminPassword: 'AdminPassword123!',
    employeeLimit: 50,
    employeeIdPrefix: '',
    enableFaceAttendance: true,
    enableQrScan: true,
    enableInstantAttendance: true
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (name === 'employeeLimit' ? Number(value) : (name === 'employeeIdPrefix' ? value.toUpperCase() : value))
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Section 1: Company Profile */}
      <div>
        <h4 className="text-xs font-bold uppercase tracking-wider text-green-700 mb-3 flex items-center space-x-1.5">
          <Building2 className="w-4 h-4" />
          <span>Company Profile</span>
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1.5">Company Name *</label>
            <input
              type="text"
              name="name"
              required
              value={formData.name}
              onChange={handleChange}
              placeholder="e.g. Acme Corporation"
              className="w-full px-3.5 py-2 rounded-xl bg-white border border-slate-100 text-xs sm:text-sm text-slate-700 focus:outline-none focus:border-green-600 transition"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1.5">Industry</label>
            <input
              type="text"
              name="industry"
              value={formData.industry}
              onChange={handleChange}
              placeholder="e.g. Fintech / Logistics"
              className="w-full px-3.5 py-2 rounded-xl bg-white border border-slate-100 text-xs sm:text-sm text-slate-700 focus:outline-none focus:border-green-600 transition"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1.5">Initial Employee Limit</label>
            <input
              type="number"
              name="employeeLimit"
              min="1"
              value={formData.employeeLimit}
              onChange={handleChange}
              className="w-full px-3.5 py-2 rounded-xl bg-white border border-slate-100 text-xs sm:text-sm text-slate-700 focus:outline-none focus:border-green-600 transition"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1.5">Employee ID Prefix *</label>
            <div className="relative">
              <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input
                type="text"
                name="employeeIdPrefix"
                required
                value={formData.employeeIdPrefix}
                onChange={handleChange}
                placeholder="e.g. ACM-EMP-"
                className="w-full pl-9 pr-3.5 py-2 rounded-xl bg-white border border-slate-100 text-xs sm:text-sm text-slate-700 focus:outline-none focus:border-green-600 transition"
              />
            </div>
            <p className="text-[10px] text-slate-500 mt-1">Static prefix for auto sequence: e.g. ACM-EMP-0001</p>
          </div>
        </div>
      </div>

      <hr className="border-slate-100/80" />

      {/* Section 2: Attendance Verification Methods */}
      <div>
        <h4 className="text-xs font-bold uppercase tracking-wider text-green-700 mb-3 flex items-center space-x-1.5">
          <ShieldCheck className="w-4 h-4" />
          <span>Attendance Check-In & Check-Out Methods</span>
        </h4>
        <p className="text-[11px] text-slate-500 mb-3">
          Select which verification methods are enabled for employees of this company.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <label className={`flex items-center space-x-3 p-3 rounded-xl border transition cursor-pointer ${formData.enableFaceAttendance ? 'bg-green-950/40 border-green-600/50' : 'bg-white border-slate-100 opacity-60'}`}>
            <input
              type="checkbox"
              name="enableFaceAttendance"
              checked={formData.enableFaceAttendance}
              onChange={handleChange}
              className="w-4 h-4 rounded text-green-700 focus:ring-green-600 bg-white border-slate-700"
            />
            <div>
              <p className="text-xs font-bold text-slate-700">Face Recognition</p>
              <p className="text-[10px] text-slate-500">AI face match</p>
            </div>
          </label>

          <label className={`flex items-center space-x-3 p-3 rounded-xl border transition cursor-pointer ${formData.enableQrScan ? 'bg-green-950/40 border-green-600/50' : 'bg-white border-slate-100 opacity-60'}`}>
            <input
              type="checkbox"
              name="enableQrScan"
              checked={formData.enableQrScan}
              onChange={handleChange}
              className="w-4 h-4 rounded text-green-700 focus:ring-green-600 bg-white border-slate-700"
            />
            <div>
              <p className="text-xs font-bold text-slate-700">QR Code Scanner</p>
              <p className="text-[10px] text-slate-500">Office QR scan</p>
            </div>
          </label>

          <label className={`flex items-center space-x-3 p-3 rounded-xl border transition cursor-pointer ${formData.enableInstantAttendance ? 'bg-green-950/40 border-green-600/50' : 'bg-white border-slate-100 opacity-60'}`}>
            <input
              type="checkbox"
              name="enableInstantAttendance"
              checked={formData.enableInstantAttendance}
              onChange={handleChange}
              className="w-4 h-4 rounded text-green-700 focus:ring-green-600 bg-white border-slate-700"
            />
            <div>
              <p className="text-xs font-bold text-slate-700">Instant Check In/Out</p>
              <p className="text-[10px] text-slate-500">1-Tap GPS Check In</p>
            </div>
          </label>
        </div>
      </div>

      <hr className="border-slate-100/80" />

      {/* Section 3: Primary Company Admin Account */}
      <div>
        <h4 className="text-xs font-bold uppercase tracking-wider text-green-700 mb-3 flex items-center space-x-1.5">
          <User className="w-4 h-4" />
          <span>Primary Admin Account</span>
        </h4>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1.5">Admin Full Name *</label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input
                type="text"
                name="adminName"
                required
                value={formData.adminName}
                onChange={handleChange}
                placeholder="e.g. Sarah Connor"
                className="w-full pl-9 pr-3.5 py-2 rounded-xl bg-white border border-slate-100 text-xs sm:text-sm text-slate-700 focus:outline-none focus:border-green-600 transition"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1.5">Admin Email *</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input
                type="email"
                name="adminEmail"
                required
                value={formData.adminEmail}
                onChange={handleChange}
                placeholder="e.g. admin@acme.com"
                className="w-full pl-9 pr-3.5 py-2 rounded-xl bg-white border border-slate-100 text-xs sm:text-sm text-slate-700 focus:outline-none focus:border-green-600 transition"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1.5">Admin Phone</label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input
                type="text"
                name="adminPhone"
                value={formData.adminPhone}
                onChange={handleChange}
                placeholder="+1 555-0192"
                className="w-full pl-9 pr-3.5 py-2 rounded-xl bg-white border border-slate-100 text-xs sm:text-sm text-slate-700 focus:outline-none focus:border-green-600 transition"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1.5">Temp Admin Password *</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input
                type="text"
                name="adminPassword"
                required
                value={formData.adminPassword}
                onChange={handleChange}
                className="w-full pl-9 pr-3.5 py-2 rounded-xl bg-white border border-slate-100 text-xs sm:text-sm text-slate-700 focus:outline-none focus:border-green-600 transition"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-end space-x-3 pt-4 border-t border-slate-100">
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          className="px-4 py-2 rounded-xl text-xs font-medium bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-slate-900 transition"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading}
          className="px-6 py-2 rounded-xl text-xs font-semibold text-white bg-green-700 hover:bg-green-600 shadow-lg shadow-green-700/30 disabled:opacity-50 transition"
        >
          {loading ? 'Onboarding Company...' : 'Create & Onboard Company'}
        </button>
      </div>
    </form>
  );
};

export default CompanyForm;
